// To-rude tosproget editor: CodeMirror 6 + Yjs (y-codemirror.next) + dimensions-farvning
// + segment-kobling + menneskelig kuratering + tegn-panel. Desktop-først.
// Tegn-panelets logik bor i tegn-panel.js (denne fil leverer kun ruder, ankre og flash) –
// se den bærende beslutning øverst i tegn-panel.js om hvorfor tegn-dataene IKKE ligger i
// Yjs-dokumentet (2 MB-loftet pr. værdi i en SQLite-backet Durable Object).
import { EditorView, keymap } from '@codemirror/view';
import { EditorState } from '@codemirror/state';
import { defaultKeymap, history, historyKeymap } from '@codemirror/commands';
import { yCollab } from 'y-codemirror.next';
import * as Y from 'yjs';
import { WebsocketProvider } from 'y-websocket';
import { resolveAnchor, makeAnchor } from './anchors.js';
import { decorationField, setDecorations, buildDecorations } from './decorations.js';
import { logout } from './api.js';
import { mountTegnPanel } from './tegn-panel.js';
import dimensioner from '../../data/dimensioner.json';

const DIMS = new Map(dimensioner.dimensioner.map((d) => [d.id, d]));
function dimColor(id) { return (DIMS.get(id) || {}).farve || '#888888'; }
function dimName(id) { return (DIMS.get(id) || {}).navn || id; }
function hexA(hex, a) {
  const m = /^#?([0-9a-f]{6})$/i.exec(hex); if (!m) return hex;
  const n = parseInt(m[1], 16);
  return `rgba(${(n >> 16) & 255},${(n >> 8) & 255},${n & 255},${a})`;
}
function el(tag, cls, txt) { const e = document.createElement(tag); if (cls) e.className = cls; if (txt != null) e.textContent = txt; return e; }
function colorFromString(s) { let h = 0; for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) >>> 0; return `hsl(${h % 360} 70% 45%)`; }

export function mountEditor(root, config, session) {
  root.innerHTML = '';
  const doc = new Y.Doc();
  const wsProto = location.protocol === 'https:' ? 'wss' : 'ws';
  const provider = new WebsocketProvider(`${wsProto}://${location.host}`, 'ws', doc, { params: { doc: config.doc } });

  const zhText = doc.getText('zh');
  const daText = doc.getText('da');
  const segments = doc.getArray('segments');
  const annotations = doc.getArray('annotations');

  provider.awareness.setLocalStateField('user', {
    name: (session.email || 'redaktør').split('@')[0],
    color: colorFromString(session.email || 'x'),
  });

  // ---- UI-skelet ----
  const app = el('div', 'app-shell');
  const header = el('header', 'app-header');
  header.appendChild(el('span', 'app-title', 'KYW · Danmarksrejsen'));
  const status = el('span', 'conn-status', '…');
  const who = el('span', 'who', session.email || '');
  const envBadge = el('span', 'env-badge env-' + (config.env || 'local'), (config.env || 'local').toUpperCase());
  const logoutBtn = el('button', 'ghost-btn', 'Log ud');
  logoutBtn.addEventListener('click', async () => { await logout(); location.reload(); });
  header.append(envBadge, status, who, logoutBtn);

  // Dimensions-vælger + kuraterings-toolbar
  const toolbar = el('div', 'toolbar');
  const dimBox = el('div', 'dim-box');
  dimBox.appendChild(el('span', 'toolbar-label', 'Dimensioner:'));
  const allDimIds = dimensioner.dimensioner.map((d) => d.id);
  const selectedDims = new Set(allDimIds); // ALLE til som default
  const dimChips = new Map();
  const allToggle = el('button', 'dim-chip dim-toggle-all', 'Alle fra');
  function syncToggleLabel() { allToggle.textContent = selectedDims.size === allDimIds.length ? 'Alle fra' : 'Alle til'; }
  allToggle.addEventListener('click', () => {
    const turnOn = selectedDims.size < allDimIds.length; // noget slukket -> taend alle; ellers sluk alle
    selectedDims.clear();
    if (turnOn) allDimIds.forEach((id) => selectedDims.add(id));
    dimChips.forEach((c, id) => c.classList.toggle('on', selectedDims.has(id)));
    syncToggleLabel();
    scheduleRecompute();
  });
  dimBox.appendChild(allToggle);
  for (const d of dimensioner.dimensioner) {
    const chip = el('button', 'dim-chip on', d.navn); // default TIL
    chip.style.setProperty('--dc', d.farve);
    chip.title = d.beskrivelse || '';
    chip.addEventListener('click', () => {
      if (selectedDims.has(d.id)) { selectedDims.delete(d.id); chip.classList.remove('on'); }
      else { selectedDims.add(d.id); chip.classList.add('on'); }
      syncToggleLabel();
      scheduleRecompute();
    });
    dimChips.set(d.id, chip);
    dimBox.appendChild(chip);
  }
  const curBox = el('div', 'cur-box');
  curBox.appendChild(el('span', 'toolbar-label', 'Tag markering:'));
  const curSelect = document.createElement('select');
  curSelect.appendChild(new Option('vælg dimension …', ''));
  for (const d of dimensioner.dimensioner) curSelect.appendChild(new Option(d.navn, d.id));
  const tagBtn = el('button', 'cur-btn', 'Tilføj tag');
  const untagBtn = el('button', 'cur-btn', 'Fjern tags i markering');
  curBox.append(curSelect, tagBtn, untagBtn);
  toolbar.append(dimBox, curBox);

  // To-rude split
  const split = el('div', 'split');
  const zhPane = el('div', 'pane pane-zh');
  const daPane = el('div', 'pane pane-da');
  zhPane.appendChild(el('div', 'pane-head', '中文 · Klassisk kinesisk'));
  daPane.appendChild(el('div', 'pane-head', 'Dansk'));
  const zhHost = el('div', 'cm-host'); const daHost = el('div', 'cm-host');
  zhPane.appendChild(zhHost); daPane.appendChild(daHost);
  split.append(zhPane, daPane);

  app.append(header, toolbar, split);
  root.appendChild(app);

  // ---- CodeMirror-ruder ----
  let activeSelection = null; // {lang, from, to}
  let activeSeg = null;
  let flash = null; // midlertidig markering ved klik i tegn-panelet: {lang, from, to}
  let flashTimer = 0;
  let tegnPanel = null; // sættes lige efter ruderne; recompute tåler null indtil da

  function makeView(host, ytext, lang) {
    const view = new EditorView({
      parent: host,
      state: EditorState.create({
        doc: ytext.toString(),
        extensions: [
          history(),
          keymap.of([...defaultKeymap, ...historyKeymap]),
          EditorView.lineWrapping,
          // Rude-klassen sættes gennem CodeMirrors egen facet, IKKE med classList.add på
          // view.dom: CodeMirror skriver hele className om når ruden får/mister fokus (den
          // tilføjer cm-focused), og fjernede dermed cm-pane-zh/-da permanent ved første klik.
          // Klasserne bærer rudernes sprog-fonte i style.css, så teksten skiftede font så snart
          // man klikkede i den. Facetten indgår i CodeMirrors egen beregning og overlever.
          EditorView.editorAttributes.of({ class: 'cm-pane cm-pane-' + lang }),
          decorationField,
          yCollab(ytext, provider.awareness),
          EditorView.updateListener.of((u) => {
            if (u.selectionSet) {
              const s = u.state.selection.main;
              activeSelection = s.empty ? null : { lang, from: s.from, to: s.to };
            }
            if (u.docChanged) scheduleRecompute();
          }),
        ],
      }),
    });
    view.dom.addEventListener('mouseup', () => onPaneClick(lang, view.state.selection.main.head));
    return view;
  }
  const zhView = makeView(zhHost, zhText, 'zh');
  const daView = makeView(daHost, daText, 'da');

  // ---- Tegn-panelet (samlet oversigt over tegn der gengives på mere end én måde) ----
  // Editoren leverer: de levende segment-ankre, begge ruder, dimensionsfarven og
  // flash-mekanismen. Alt andet (indeks, liste, CSV, doven hentning af /api/tegn) bor i
  // tegn-panel.js, så denne fil bliver mindre, ikke større.
  function flashRange(lang, from, to) {
    const view = lang === 'zh' ? zhView : daView;
    view.dispatch({ effects: EditorView.scrollIntoView(from, { y: 'center' }) });
    flash = { lang, from, to };
    clearTimeout(flashTimer);
    flashTimer = setTimeout(() => { flash = null; scheduleRecompute(); }, 1800);
    scheduleRecompute();
  }
  tegnPanel = mountTegnPanel({
    doc,
    segments,
    resolveAnchor,
    zhView,
    daView,
    farve: dimColor('tegn'),
    onFlash: flashRange,
    requestRender: scheduleRecompute,
  });
  app.appendChild(tegnPanel.el);

  // ---- Dekorations-genberegning (debounced) ----
  let raf = 0;
  function scheduleRecompute() { if (raf) return; raf = requestAnimationFrame(() => { raf = 0; recompute(); }); }

  function recompute() {
    const zhItems = [], daItems = [];
    annotations.forEach((ann) => {
      if (selectedDims.size && !selectedDims.has(ann.dimension)) return;
      if (!selectedDims.size) return; // intet valgt => ingen dimensions-farve
      const abs = resolveAnchor(ann.anchor, doc);
      if (!abs) return; // kollapset anker => fremhævning forsvinder
      const color = dimColor(ann.dimension);
      const item = {
        from: abs[0], to: abs[1], class: 'ann',
        attributes: { style: `background-color:${hexA(color, 0.22)};border-bottom:2px solid ${color};`, title: `${dimName(ann.dimension)}${ann.note ? ' · ' + ann.note : ''}${ann.source === 'human' ? ' (kurateret)' : ''}` },
      };
      (ann.lang === 'zh' ? zhItems : daItems).push(item);
    });
    if (activeSeg) {
      const z = resolveAnchor(activeSeg.zhAnchor, doc); if (z) zhItems.push({ from: z[0], to: z[1], class: 'seg-active', attributes: {} });
      const d = resolveAnchor(activeSeg.daAnchor, doc); if (d) daItems.push({ from: d[0], to: d[1], class: 'seg-active', attributes: {} });
    }
    // Åbent tegn i tegn-panelet: fremhæv NETOP det tegns enheder + danske gengivelser –
    // aldrig alle tegn på én gang (arbejdet er bundet til det åbne tegn). sync() holder
    // samtidig panelets liste ajour (cache-gated); den kaldes derfor også når
    // tegn-dimensionen er slået fra i toolbaren, hvor kun farvelægningen udelades.
    const tegnHits = tegnPanel ? tegnPanel.sync() : [];
    if (selectedDims.has('tegn')) {
      const tc = dimColor('tegn');
      for (const h of tegnHits) {
        (h.lang === 'zh' ? zhItems : daItems).push({
          from: h.from, to: h.to, class: 'tegn-ann',
          attributes: { style: `background-color:${hexA(tc, 0.25)};border-bottom:2px solid ${tc};` },
        });
      }
    }
    if (flash) (flash.lang === 'zh' ? zhItems : daItems).push({ from: flash.from, to: flash.to, class: 'tegn-flash', attributes: {} });
    zhView.dispatch({ effects: setDecorations.of(buildDecorations(zhItems, zhView.state.doc.length)) });
    daView.dispatch({ effects: setDecorations.of(buildDecorations(daItems, daView.state.doc.length)) });
  }

  // ---- Segment-kobling ----
  function findSegmentAt(lang, pos) {
    let found = null;
    segments.forEach((seg) => {
      const abs = resolveAnchor(lang === 'zh' ? seg.zhAnchor : seg.daAnchor, doc);
      if (abs && pos >= abs[0] && pos <= abs[1]) found = seg;
    });
    return found;
  }
  function onPaneClick(lang, pos) {
    activeSeg = findSegmentAt(lang, pos);
    recompute();
    if (activeSeg) {
      const otherLang = lang === 'zh' ? 'da' : 'zh';
      const abs = resolveAnchor(otherLang === 'zh' ? activeSeg.zhAnchor : activeSeg.daAnchor, doc);
      const view = otherLang === 'zh' ? zhView : daView;
      if (abs) view.dispatch({ effects: EditorView.scrollIntoView(abs[0], { y: 'center' }) });
    }
  }

  // ---- Menneskelig kuratering ----
  tagBtn.addEventListener('click', () => {
    const dim = curSelect.value;
    if (!dim || !activeSelection) return;
    const { lang, from, to } = activeSelection;
    if (from >= to) return;
    const ytext = lang === 'zh' ? zhText : daText;
    annotations.push([{
      id: 'h' + Date.now().toString(36) + Math.random().toString(36).slice(2, 5),
      dimension: dim, lang, anchor: makeAnchor(ytext, from, to), note: '', source: 'human',
    }]);
    if (!selectedDims.has(dim)) { selectedDims.add(dim); const c = dimChips.get(dim); if (c) c.classList.add('on'); syncToggleLabel(); }
    scheduleRecompute();
  });
  untagBtn.addEventListener('click', () => {
    if (!activeSelection) return;
    const { lang, from, to } = activeSelection;
    const del = [];
    annotations.forEach((ann, idx) => {
      if (ann.lang !== lang) return;
      const abs = resolveAnchor(ann.anchor, doc);
      if (abs && abs[0] < to && abs[1] > from) del.push(idx);
    });
    del.sort((a, b) => b - a).forEach((idx) => annotations.delete(idx, 1));
    scheduleRecompute();
  });

  // ---- Reaktivitet ----
  annotations.observeDeep(scheduleRecompute);
  segments.observeDeep(scheduleRecompute);
  provider.on('status', (e) => { status.textContent = e.status === 'connected' ? '● forbundet' : '○ afbrudt'; status.className = 'conn-status ' + e.status; });
  provider.on('sync', (isSynced) => { if (isSynced) scheduleRecompute(); });

  scheduleRecompute();
  return { doc, provider, destroy() { clearTimeout(flashTimer); tegnPanel.destroy(); provider.destroy(); zhView.destroy(); daView.destroy(); } };
}
